import { env } from "./util/env";

export function zendeskChatConfig() {
  sessionStorage.setItem("zendeskAccountId", env.VITE_ZENDESK_ACCOUNT_ID ?? "");
  sessionStorage.setItem("zendeskDepartmentId", env.VITE_ZENDESK_DEPARTMENT_ID ?? "");

  // Dispatch zopim initialization event
  const zopimInit = new Event("zopimInit");
  document.dispatchEvent(zopimInit);

  // Set up listener to detect when user sends messages in chat
  // Monitor DOM for send button clicks (note - focus on element plus Enter or Space key counts as a click)
  const setupMessageSentTracking = () => {
    const widgetContainer = document.getElementById("zopim-widget");
    if (!widgetContainer) {
      return;
    }

    const dispatchMessageSentEvent = (messageType: "offline" | "online") => {
      const messageSentEvent = new CustomEvent("zopimMessageSent", {
        detail: { messageType },
      });
      window.dispatchEvent(messageSentEvent);
    };

    const checkAndDispatchEvent = (form: HTMLFormElement | null) => {
      if (!form) return;

      const textarea = form.querySelector("textarea") as HTMLTextAreaElement;
      if (!textarea || textarea.value.trim().length === 0) {
        return;
      }

      // Determine message type based on button class in form
      const sendButton = form.querySelector(".send-button, .button-send");
      const messageType = sendButton?.classList.contains("button-send") ? "offline" : "online";

      // Dispatch after a small delay to ensure message is sent
      setTimeout(() => dispatchMessageSentEvent(messageType), 100);
    };

    // Listen for button clicks (note - element focus plus Enter or Space key counts as a click)
    widgetContainer.addEventListener(
      "click",
      event => {
        const target = event.target as HTMLElement;
        // Check if clicked element is a send button or inside one
        const button = target.closest(".send-button, .button-send");
        if (button) {
          const form = button.closest("form") as HTMLFormElement;
          checkAndDispatchEvent(form);
        }
      },
      true,
    );

    // Listen for Enter key in textareas
    widgetContainer.addEventListener(
      "keydown",
      event => {
        const target = event.target as HTMLElement;

        // Check if Enter was pressed (without Shift) in a textarea
        if (target.tagName === "TEXTAREA" && event.key === "Enter" && !event.shiftKey) {
          const form = target.closest("form") as HTMLFormElement;
          checkAndDispatchEvent(form);
        }
      },
      true,
    );

    // Listen for "End Chat" button clicks (part of widget.js, not React)
    widgetContainer.addEventListener(
      "click",
      event => {
        const target = event.target as HTMLElement;
        const endChatButton = target.closest(".end-chat");

        if (endChatButton) {
          // Dispatch custom event that the context will pick up
          const chatClosedEvent = new CustomEvent("zopimChatClosed");
          window.dispatchEvent(chatClosedEvent);
        }
      },
      true,
    );
  };

  // Track if listener is already attached to avoid duplicates
  let listenerAttached = false;

  // Set up tracking and watch for widget changes
  const observeAndSetup = () => {
    const widgetContainer = document.getElementById("zopim-widget");
    if (widgetContainer && !listenerAttached) {
      setupMessageSentTracking();
      listenerAttached = true;
    }
  };

  // Use MutationObserver to detect when widget appears or content changes
  const observer = new MutationObserver(mutations => {
    // Check if the widget was added or modified
    for (const mutation of mutations) {
      if (mutation.type === "childList") {
        const widgetContainer = document.getElementById("zopim-widget");
        if (widgetContainer) {
          // Check if the send button exists (indicates widget is ready)
          const sendButton = widgetContainer.querySelector(".send-button, .button-send");
          if (sendButton && !listenerAttached) {
            setupMessageSentTracking();
            listenerAttached = true;
          } else if (!sendButton && listenerAttached) {
            // Widget was cleared/replaced, reset flag so we can re-attach
            listenerAttached = false;
          }
        }
      }
    }
  });

  // Observe document for widget changes
  observer.observe(document.body, { childList: true, subtree: true });

  // Also try immediately in case widget already exists
  setTimeout(observeAndSetup, 1000);
}
